import { TenantId } from '@pelicargo/tenants';
import { useCallback } from 'react';

import { CustomerTheme, ThemeFormValues, toThemeJson } from '../../components/Theme/form';
import { trpc } from '../../config/trpc';

type UseCustomerTheme = {
  theme?: CustomerTheme;
  isLoading: boolean;
  isSaving: boolean;
  save: (values: ThemeFormValues) => Promise<CustomerTheme>;
};

/**
 * One tenant's customer theme, plus the save that serialises it. Both procedures are gated on
 * `isSuperAdminOrSystemTenant` server-side.
 */
export const useCustomerTheme = (tenantId?: TenantId): UseCustomerTheme => {
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.getTenantCustomerTheme.useQuery(
    { tenant_id: tenantId as TenantId },
    // Refetching on focus would re-seed the form and throw away whatever is being typed.
    { enabled: !!tenantId, refetchOnWindowFocus: false },
  );

  const { mutateAsync, isLoading: isSaving } = trpc.updateTenantCustomerTheme.useMutation();

  const save = useCallback(
    async (values: ThemeFormValues) => {
      // `toThemeJson` strips the empty fields. An absent key is what restores the tenant's static
      // baseline through the theme's `var()` fallback, so a cleared field has to reach the API as
      // a missing key rather than an empty string.
      const theme = await mutateAsync({ tenant_id: tenantId as TenantId, value: toThemeJson(values) });
      await utils.getTenantCustomerTheme.invalidate();

      return theme;
    },
    [mutateAsync, tenantId, utils],
  );

  return { theme: data, isLoading, isSaving, save };
};
